import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { carpoolsAPI } from '../services/api';
import { PageLoading, PageHead, PageEmpty, PageListCard, ActivityIconBadge } from '../components/appPage';
import './Carpools.css';

const Carpools = () => {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const [carpools, setCarpools] = useState([]);
  const [discoverCarpools, setDiscoverCarpools] = useState([]);
  const [discoverArea, setDiscoverArea] = useState(null);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [joiningId, setJoiningId] = useState('');
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const canDiscover = Boolean(isAdmin || usageStats?.isPremium || user?.isPremium);

  useEffect(() => {
    loadCarpools();
    loadUsageStats();
  }, []);

  useEffect(() => {
    if (canDiscover) {
      loadDiscoverCarpools();
    }
  }, [canDiscover]);

  const loadCarpools = async () => {
    try {
      const response = await carpoolsAPI.getCarpools();
      setCarpools(response.data.carpools || []);
    } catch (error) {
      console.error('Failed to load carpools:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDiscoverCarpools = async () => {
    try {
      setDiscoverLoading(true);
      const response = await carpoolsAPI.discoverCarpools();
      setDiscoverCarpools(response.data.carpools || []);
      setDiscoverArea(response.data.area || null);
    } catch (error) {
      console.error('Failed to load discover carpools:', error);
    } finally {
      setDiscoverLoading(false);
    }
  };

  const loadUsageStats = async () => {
    try {
      const response = await carpoolsAPI.getUsageStats();
      setUsageStats(response.data || null);
    } catch (error) {
      console.error('Failed to load usage stats:', error);
    }
  };

  const handleJoinCarpool = async (carpoolId) => {
    try {
      setJoiningId(carpoolId);
      await carpoolsAPI.joinCarpool(carpoolId);
      await Promise.all([loadCarpools(), loadDiscoverCarpools()]);
    } catch (error) {
      console.error('Failed to join carpool:', error);
    } finally {
      setJoiningId('');
    }
  };

  if (loading) {
    return (
      <div className="carpools-page page-shell">
        <PageLoading message={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="carpools-page page-shell">
      <PageHead
        title={t('carpools.title')}
        lead={t(
          'carpools.listLead',
          'Преглед и управление на всички ваши групи за споделени превози.'
        )}
        actions={
          <Link to="/carpools/new" className="page-btn page-btn--primary">
            <Plus size={18} strokeWidth={2} aria-hidden />
            {t('carpools.create')}
          </Link>
        }
      />

      {carpools.length === 0 ? (
        <PageEmpty
          text={t('dashboard.noCarpools')}
          action={
            <Link to="/carpools/new" className="btn-primary">
              {t('dashboard.createFirst')}
            </Link>
          }
        />
      ) : (
        <ul className="page-list carpools-list">
          {carpools.map((carpool) => (
            <li key={carpool.id} className="carpools-list__row">
              <PageListCard
                to={`/carpools/${carpool.id}`}
                icon={<ActivityIconBadge activity={carpool.activity} size={22} />}
                title={carpool.name}
                meta={`${carpool.schedule?.time || '—'} · ${carpool.address || '—'} · ${
                  carpool.members?.length || 0
                } ${t('carpools.membersCountSuffix', 'членове')}`}
                trailing={<span className="page-pill">{t('common.active', 'Активен')}</span>}
              />
              <Link
                to={`/prototype-details/${carpool.id}`}
                className="page-btn page-btn--ghost carpools-list__prototype-btn"
                title={t('carpools.openPrototype')}
              >
                <Sparkles size={16} strokeWidth={2} aria-hidden />
                <span>{t('carpools.openPrototype')}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {canDiscover && (
        <section className="carpools-discover">
          <div className="carpools-discover__head">
            <h2>{t('carpools.discoverTitle', 'Карпули във вашия район')}</h2>
            {discoverArea && (
              <span className="page-pill">
                {t('carpools.discoverArea', { area: discoverArea, defaultValue: `Район: ${discoverArea}` })}
              </span>
            )}
          </div>

          {discoverLoading ? (
            <PageLoading message={t('common.loading')} minHeight={false} />
          ) : discoverCarpools.length === 0 ? (
            <PageEmpty
              text={
                discoverArea
                  ? t('carpools.discoverEmpty', 'Няма свободни карпули във вашия район.')
                  : t('carpools.discoverNeedArea', 'Добавете адрес в профила или се включете в карпул, за да определим района.')
              }
            />
          ) : (
            <ul className="page-list carpools-list">
              {discoverCarpools.map((carpool) => (
                <li key={`discover-${carpool.id}`} className="carpools-list__row">
                  <PageListCard
                    to={`/carpools/${carpool.id}`}
                    icon={<ActivityIconBadge activity={carpool.activity} size={22} />}
                    title={carpool.name}
                    meta={`${carpool.schedule?.time || '—'} · ${carpool.address || '—'} · ${
                      carpool.members?.length || 0
                    } ${t('carpools.membersCountSuffix', 'членове')}`}
                  />
                  <button
                    type="button"
                    className="page-btn page-btn--primary carpools-list__join-btn"
                    onClick={() => handleJoinCarpool(carpool.id)}
                    disabled={joiningId === carpool.id || carpool.isMember}
                  >
                    {carpool.isMember
                      ? t('carpools.alreadyMember', 'Вече сте член')
                      : joiningId === carpool.id
                      ? t('common.loading')
                      : t('carpools.joinNearby', 'Присъедини се')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
};

export default Carpools;
